<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const i18nKeyPrefix = 'PROFILE_SETTINGS.FORM.CUSTOM_SLA_ALERTS_SECTION';

const alertsEnabled = ref(false);
const percentage = ref(50);

watch(
  uiSettings,
  value => {
    alertsEnabled.value = !!value.custom_sla_alerts_enabled;
    percentage.value = value.custom_sla_alert_percentage || 50;
  },
  { immediate: true }
);

const showPermissionWarning = computed(
  () =>
    alertsEnabled.value &&
    'Notification' in window &&
    Notification.permission === 'denied'
);

const handleToggle = value => {
  alertsEnabled.value = value;
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
  updateUISettings({ custom_sla_alerts_enabled: value });
  useAlert(t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
};

const handlePercentageChange = event => {
  const value = Math.min(99, Math.max(1, Number(event.target.value) || 50));
  percentage.value = value;
  updateUISettings({ custom_sla_alert_percentage: value });
  useAlert(t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center gap-2">
      <ToggleSwitch
        :model-value="alertsEnabled"
        @update:model-value="handleToggle"
      />
      <span class="text-sm text-n-slate-12">
        {{ $t(`${i18nKeyPrefix}.ENABLE_LABEL`) }}
      </span>
    </div>
    <div v-if="alertsEnabled" class="flex items-center gap-2">
      <label
        class="text-sm text-n-slate-12 mb-0"
        for="custom-sla-alert-percentage"
      >
        {{ $t(`${i18nKeyPrefix}.PERCENTAGE_LABEL`) }}
      </label>
      <input
        id="custom-sla-alert-percentage"
        type="number"
        min="1"
        max="99"
        :value="percentage"
        class="w-20 rounded-lg border-0 ring-1 ring-inset ring-n-weak focus:ring-2 focus:ring-n-brand bg-n-alpha-black2 px-2 py-1 text-sm text-n-slate-12"
        @change="handlePercentageChange"
      />
    </div>
    <p v-if="showPermissionWarning" class="text-sm text-n-amber-11">
      {{ $t(`${i18nKeyPrefix}.PERMISSION_DENIED`) }}
    </p>
  </div>
</template>
